import { z } from 'zod'
import { timestampSchema } from './common.js'

// First Response dispatch schemas. Operatives and jobs are sourced from n8n
// webhooks server-side (see @rapid-response/api `dispatch.ts`); these describe
// the normalised shapes returned to REST + RPC callers.

export const latLonSchema = z.object({
  lat: z.number(),
  lon: z.number(),
})
export type LatLon = z.infer<typeof latLonSchema>

/** A field officer near the asset, with road distance + driving time. */
export const nearbyOperativeSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  username: z.string().nullable(),
  partnerName: z.string(),
  partnerId: z.string().nullable(),
  phone: z.string().nullable(),
  distanceMiles: z.number(),
  durationSeconds: z.number(),
  lat: z.number(),
  lon: z.number(),
  lastSeen: z.string().nullable(),
})
export type NearbyOperative = z.infer<typeof nearbyOperativeSchema>

export const operativesResponseSchema = z.object({
  operatives: z.array(nearbyOperativeSchema),
  assetLocation: latLonSchema.nullable(),
  assetNumber: z.number().int().nullable(),
  assetPostcode: z.string().nullable(),
})
export type OperativesResponse = z.infer<typeof operativesResponseSchema>

/** A job near the asset. */
export const nearbyJobSchema = z.object({
  id: z.string(),
  jobId: z.string().nullable(),
  jobNumber: z.string(),
  service: z.string(),
  executionStatus: z.string(),
  allocatedTo: z.string().nullable(),
  partnerName: z.string(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  distanceMiles: z.number().nullable(),
  jobDate: z.string().nullable(),
  jobDateEx: z.string().nullable(),
})
export type NearbyJob = z.infer<typeof nearbyJobSchema>

export const jobsResponseSchema = z.object({
  jobs: z.array(nearbyJobSchema),
  assetLocation: latLonSchema.nullable(),
  assetNumber: z.number().int().nullable(),
  assetPostcode: z.string().nullable(),
})
export type JobsResponse = z.infer<typeof jobsResponseSchema>

// Query params (REST). Job numbers are alphanumeric EGIS identifiers (e.g.
// "J21303142"), so they stay strings; radius/interval coerce from the query.
export const operativesQuerySchema = z.object({
  jobNumber: z.string().trim().min(1),
})
export type OperativesQuery = z.infer<typeof operativesQuerySchema>

export const jobsQuerySchema = z.object({
  jobNumber: z.string().trim().min(1),
  radius: z.coerce.number().int().min(1).max(30).default(30),
  interval: z.coerce.number().int().min(1).default(60),
})
export type JobsQuery = z.infer<typeof jobsQuerySchema>

// Resolve a searched job number to its property (asset). The search enters via
// the jobs table and joins to the asset; an unresolvable job is an error.
export const resolvePropertyQuerySchema = z.object({
  jobNumber: z.string().trim().min(1),
})
export type ResolvePropertyQuery = z.infer<typeof resolvePropertyQuerySchema>

export const resolvedPropertySchema = z.object({
  jobNumber: z.string(),
  assetNumber: z.number().int(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  postcode: z.string().nullable(),
})
export type ResolvedProperty = z.infer<typeof resolvedPropertySchema>

// ── Persisted dispatch sessions, outcomes & lookups ────────

/** Stable identity for an operative within a session — phone if present, else
 * a normalised name. The live operative `id` is position-dependent (see
 * @rapid-response/api dispatch.ts) and must not be used to anchor outcomes. */
export function deriveOfficerKey(op: {
  phone: string | null
  firstName: string
  lastName: string
}): string {
  const phone = op.phone?.trim()
  if (phone) return phone
  return `${op.firstName} ${op.lastName}`.trim().toLowerCase()
}

export const dispatchTargetTypeSchema = z.enum(['officer', 'job'])
export type DispatchTargetType = z.infer<typeof dispatchTargetTypeSchema>

export const dispatchOutcomeStatusSchema = z.enum([
  'call_attempted', // a call/contact was attempted (officer or job)
  'accepted', // accepted (terminal); officer outcomes capture acceptedJob
  'declined', // declined (non-terminal — can later be accepted)
])
export type DispatchOutcomeStatus = z.infer<typeof dispatchOutcomeStatusSchema>

export const dispatchLookupKindSchema = z.enum(['operatives', 'jobs'])
export type DispatchLookupKind = z.infer<typeof dispatchLookupKindSchema>

/** Who performed an action; null for application-token callers (no identity). */
export const actorRefSchema = z.object({ id: z.string(), name: z.string() })
export type ActorRef = z.infer<typeof actorRefSchema>

export const dispatchSessionSchema = z.object({
  id: z.string(),
  // The job number the dispatcher searched (alphanumeric EGIS id, e.g.
  // "J21303142"). Nullable for sessions created before job-number search
  // existed; always set for new sessions.
  jobNumber: z.string().nullable(),
  // The property (asset) the job resolves to — display + analytics anchor.
  assetNumber: z.number().int(),
  radiusMiles: z.number().int(),
  intervalMin: z.number().int(),
  assetLat: z.number().nullable(),
  assetLon: z.number().nullable(),
  createdBy: actorRefSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type DispatchSession = z.infer<typeof dispatchSessionSchema>

export const dispatchOutcomeSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  targetType: dispatchTargetTypeSchema,
  targetKey: z.string(),
  label: z.string(),
  status: dispatchOutcomeStatusSchema,
  acceptedJob: z.string().nullable(),
  recordedBy: actorRefSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type DispatchOutcome = z.infer<typeof dispatchOutcomeSchema>

export const dispatchLookupSchema = z.object({
  id: z.string(),
  sessionId: z.string().nullable(),
  kind: dispatchLookupKindSchema,
  // Logged request params (jobNumber, plus radius/interval for jobs) — a JSON-serializable
  // value map, not `unknown`, so the RPC return type stays serializable.
  request: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  resultCount: z.number().int(),
  durationMs: z.number().int().nullable(),
  status: z.enum(['ok', 'error']),
  error: z.string().nullable(),
  calledBy: actorRefSchema.nullable(),
  createdAt: timestampSchema,
})
export type DispatchLookup = z.infer<typeof dispatchLookupSchema>

export const dispatchSessionBundleSchema = z.object({
  session: dispatchSessionSchema,
  outcomes: z.array(dispatchOutcomeSchema),
  lookups: z.array(dispatchLookupSchema),
})
export type DispatchSessionBundle = z.infer<typeof dispatchSessionBundleSchema>

// REST/RPC inputs.
export const createDispatchSessionInputSchema = z.object({
  // Caller supplies the searched job number and the property it resolved to
  // (see resolvedPropertySchema). Resolution is a separate, EGIS-backed step so
  // session creation itself stays a pure write.
  jobNumber: z.string().trim().min(1),
  assetNumber: z.coerce.number().int(),
  radius: z.coerce.number().int().min(1).max(30).default(10),
  interval: z.coerce.number().int().min(1).default(60),
  assetLat: z.number().nullable().optional(),
  assetLon: z.number().nullable().optional(),
})
export type CreateDispatchSessionInput = z.infer<typeof createDispatchSessionInputSchema>

export const recordDispatchOutcomeInputSchema = z.object({
  targetType: dispatchTargetTypeSchema,
  targetKey: z.string().min(1),
  label: z.string().min(1),
  status: dispatchOutcomeStatusSchema,
  acceptedJob: z.string().nullable().optional(),
})
export type RecordDispatchOutcomeInput = z.infer<typeof recordDispatchOutcomeInputSchema>

// Clearing an outcome only needs to identify the target — its inverse,
// recordOutcome, upserts; this removes the row entirely.
export const clearDispatchOutcomeInputSchema = z.object({
  targetType: dispatchTargetTypeSchema,
  targetKey: z.string().min(1),
})
export type ClearDispatchOutcomeInput = z.infer<typeof clearDispatchOutcomeInputSchema>
